import { useTranslation } from 'react-i18next';

import { Panel } from 'components/containers';
import OptionallyVisible from 'components/optionallyVisible';

import { OrderItemType, RefundItemStorageModel } from 'types/pwcOrder';

import { LABELS } from '../../keys';
import { OrderPwcSectionProps } from '../../types';
import { AmountBlock, AmountNumericValue, AmountValue, Item, ItemName, ItemValue, OrderItems, SectionWrapper } from '../styles';
import { formatNumber } from '../utils';
import { OrderItem } from './components/orderItem';

export const RefundSummarySection = ({ orderData, rowId }: OrderPwcSectionProps) => {
  const { t } = useTranslation();

  const refund = orderData?.refunds.find((refund) => refund.uuid === rowId);

  const getItemByType = (type: OrderItemType): RefundItemStorageModel | undefined => {
    return refund?.items?.find((item) => item.type === type);
  };

  const customItems = refund?.items?.filter((item) => item.type === OrderItemType.Custom) || [];

  const discountItems = refund?.items?.filter((item) => item.type === OrderItemType.Discount) || [];

  return (
    <Panel label={t(LABELS.SECTIONS.REFUND_SUMMARY.TITLE)}>
      <SectionWrapper>
        <OptionallyVisible visible={Boolean(refund?.items?.length)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_SUMMARY.ITEMS)}</ItemName>
            <ItemValue>
              <OrderItems>
                {refund?.items
                  ?.filter((item) => item.type === OrderItemType.Product)
                  .map((item) => <OrderItem key={item.uuid} {...item} currency={refund.fiatCurrencyCode!} />)}
              </OrderItems>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(getItemByType(OrderItemType.Tax))}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_SUMMARY.TAX)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountValue>{refund?.fiatCurrencyCode?.toUpperCase()}</AmountValue>
                <AmountNumericValue>{formatNumber(getItemByType(OrderItemType.Tax)?.fiatAmount)}</AmountNumericValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(getItemByType(OrderItemType.Delivery))}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_SUMMARY.DELIVERY_FEE)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountValue>{refund?.fiatCurrencyCode?.toUpperCase()}</AmountValue>
                <AmountNumericValue>{formatNumber(getItemByType(OrderItemType.Delivery)?.fiatAmount)}</AmountNumericValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(getItemByType(OrderItemType.Tip))}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_SUMMARY.TIP)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountValue>{refund?.fiatCurrencyCode?.toUpperCase()}</AmountValue>
                <AmountNumericValue>{formatNumber(getItemByType(OrderItemType.Tip)?.fiatAmount)}</AmountNumericValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(getItemByType(OrderItemType.Gratuity))}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_SUMMARY.GRATUITY)}</ItemName>
            <ItemValue>
              <AmountBlock>
                <AmountValue>{refund?.fiatCurrencyCode?.toUpperCase()}</AmountValue>
                <AmountNumericValue>{formatNumber(getItemByType(OrderItemType.Gratuity)?.fiatAmount)}</AmountNumericValue>
              </AmountBlock>
            </ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(customItems.length)}>
          {customItems.map((item) => (
            <Item key={item.uuid}>
              <ItemName>{item.name}</ItemName>
              <ItemValue>
                <AmountBlock>
                  <AmountValue>{refund?.fiatCurrencyCode?.toUpperCase()}</AmountValue>
                  <AmountNumericValue>{formatNumber(item?.fiatAmount)}</AmountNumericValue>
                </AmountBlock>
              </ItemValue>
            </Item>
          ))}
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(discountItems.length)}>
          {discountItems.map((item) => (
            <Item key={item.uuid}>
              <ItemName>{item.name}</ItemName>
              <ItemValue>
                <AmountBlock>
                  <AmountValue>{refund?.fiatCurrencyCode?.toUpperCase()}</AmountValue>
                  <AmountNumericValue>{formatNumber(item?.fiatAmount)}</AmountNumericValue>
                </AmountBlock>
              </ItemValue>
            </Item>
          ))}
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.redirectUrl)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_SUMMARY.REDIRECT_URL)}</ItemName>
            <ItemValue>{refund?.redirectUrl}</ItemValue>
          </Item>
        </OptionallyVisible>

        <OptionallyVisible visible={Boolean(refund?.cancelRedirectUrl)}>
          <Item>
            <ItemName>{t(LABELS.SECTIONS.REFUND_SUMMARY.CANCEL_REDIRECT_URL)}</ItemName>
            <ItemValue>{refund?.cancelRedirectUrl}</ItemValue>
          </Item>
        </OptionallyVisible>
      </SectionWrapper>
    </Panel>
  );
};
